import React from "react";
import "./AboutPage.css";
import AboutCard from "../components/AboutCard.js";
import Raina from "../images/Raina.jpg";
import Aishwarya from "../images/Aishwarya.jpeg";
import Beau from "../images/Beau.jpg";

function AboutPage() {
  const GITLAB_API_URL = "https://gitlab.com/api/v4";
  const GITLAB_TOKEN = process.env.REACT_APP_GITLAB_TOKEN;
  const PROJECT_ID = "66984819";

  const [project, setProject] = React.useState(null);
  const [members, setMembers] = React.useState([]);

  React.useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const projectRes = await fetch(
          `${GITLAB_API_URL}/projects/${PROJECT_ID}?statistics=true`,
          {
            headers: {
              Authorization: `Bearer ${GITLAB_TOKEN}`,
            },
          }
        );
        const projectData = await projectRes.json();
        setProject(projectData);
      } catch (error) {
        console.error("Error fetching GitLab data:", error);
      }
    };

    const fetchMemberStats = async () => {
      try {
        const membersRes = await fetch(
          `${GITLAB_API_URL}/projects/${PROJECT_ID}/members/all`,
          {
            headers: { Authorization: `Bearer ${GITLAB_TOKEN}` },
          }
        );
        const membersData = await membersRes.json();

        // Filter for valid members if necessary
        const validMembers = [
          "Raina A Parikh",
          "Aishwarya Balaji",
          "Beau Cheatham",
        ];
        const filteredMembers = membersData.filter((member) =>
          validMembers.includes(member.name)
        );

        // Helper function to fetch all commits with pagination
        const fetchAllCommits = async () => {
          let allCommits = [];
          let page = 1;
          let perPage = 100; // GitLab max limit per request
          let hasMoreCommits = true;

          while (hasMoreCommits) {
            const commitsRes = await fetch(
              `${GITLAB_API_URL}/projects/${PROJECT_ID}/repository/commits?per_page=${perPage}&page=${page}`,
              { headers: { Authorization: `Bearer ${GITLAB_TOKEN}` } }
            );

            if (!commitsRes.ok) {
              console.error("Error fetching commits:", commitsRes.statusText);
              break;
            }

            const commitsData = await commitsRes.json();
            allCommits = allCommits.concat(commitsData);

            if (commitsData.length < perPage) {
              hasMoreCommits = false;
            } else {
              page++;
            }
          }

          return allCommits;
        };

        const allCommits = await fetchAllCommits();

        const getFirstName = (name) => name.split(" ")[0].toLowerCase();

        const memberStats = await Promise.all(
          filteredMembers.map(async (member) => {
            const userCommits = allCommits.filter((commit) =>
              getFirstName(commit.author_name).includes(
                getFirstName(member.name)
              )
            );

            const mergeRequestsRes = await fetch(
              `${GITLAB_API_URL}/projects/${PROJECT_ID}/merge_requests?author_id=${member.id}`,
              { headers: { Authorization: `Bearer ${GITLAB_TOKEN}` } }
            );
            const mergeRequestsData = await mergeRequestsRes.json();

            const issuesRes = await fetch(
              `${GITLAB_API_URL}/projects/${PROJECT_ID}/issues?author_id=${member.id}`,
              { headers: { Authorization: `Bearer ${GITLAB_TOKEN}` } }
            );
            const issuesData = await issuesRes.json();

            const jobsRes = await fetch(
              `${GITLAB_API_URL}/projects/${PROJECT_ID}/jobs?per_page=${100}`,
              { headers: { Authorization: `Bearer ${GITLAB_TOKEN}` } }
            );
            const jobsData = await jobsRes.json();

            const filteredJobs = jobsData.filter(
              (job) => job.user.id === member.id
            );

            // Only modifying the about logic here:
            let about = "";
            if (member.name === "Raina A Parikh") {
              about =
                "I am a junior studying computer science! In my free time I enjoy reading and watching Longhorn football!";
            } else if (member.name === "Aishwarya Balaji") {
              about =
                "I am a second-year computer science major at UT-Austin. In my free time, I enjoy exploring Austin, watching movies, and baking.";
            } else if (member.name === "Beau Cheatham") {
              about =
                "I am a third-year computer science major at UT. My favorite hobbies are playing bass guitar and cooking.";
            } else if (member.name === "Luis Gamino") {
              about =
                "I am a third-year computer science major. During my free time I love hanging out with friends, hosting game nights and karaoke.";
            } else {
              about = "About information not available.";
            }

            return {
              ...member,
              commits: userCommits.length,
              mergeRequests: mergeRequestsData.length,
              issues: issuesData.length,
              tests: filteredJobs.length,
              about,
            };
          })
        );

        setMembers(memberStats);
      } catch (error) {
        console.error("Error fetching GitLab member stats:", error);
      }
    };

    fetchMemberStats();
    fetchProjectData();
  }, [GITLAB_TOKEN]);

  return (
    <div className="container mt-4">
      <h1 className="text-center mb-4">About Us</h1>

      <div className="mb-3">
        <b>Group Name: </b> {project?.name}
      </div>

      <div className="mb-3">
        <b>Total Commits: </b> {project?.statistics?.commit_count}
      </div>

      <div className="mb-3">
        <b>Our Mission: </b>
        <p>
          HelpingHand is a platform focused on raising awareness about
          underprivileged areas in Texas while connecting individuals with
          health and wellness resources via nonprofits and local health centers.
        </p>
      </div>

      <div className="mb-3">
        <b>Data Sources: </b>
        <ul>
          <li>
            <a
              href="https://www.census.gov/data/developers/data-sets/acs-1year.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Poverty Rates API
            </a>
          </li>
          <li>
            <a
              href="https://docs.every.org/docs/endpoints/nonprofit-search"
              target="_blank"
              rel="noopener noreferrer"
            >
              Nonprofits RESTful API
            </a>
          </li>
          <li>
            <a
              href="https://data.hrsa.gov/data/services"
              target="_blank"
              rel="noopener noreferrer"
            >
              Healthcare Centers API
            </a>
          </li>
        </ul>
      </div>

      <div className="mb-3">
        <b>Intended Users: </b>
        <p>
          People looking to help impoverished communities across Texas by
          understanding challenges that they face and methods of donation.
        </p>
      </div>

      <div className="mb-3">
        <b>Tools Used: </b>
        <ul>
          <li>React: created components and designed frontend</li>
          <li>HTML: created the structure of the frontend</li>
          <li>CSS: used to design and format the frontend</li>
          <li>AWS: hosting platform</li>
          <li>Postman: used to document the HelpingHand API</li>
        </ul>
      </div>

      <div className="mb-3">
        <b>Interesting Findings: </b>
        <p>
          Exploring how locations of healthcare centers differ depending on the
          county's poverty level.
        </p>
      </div>

      <div className="mb-3">
        <b>HelpingHand API: </b>
        <a
          href="https://documenter.getpostman.com/view/32140867/2sAYXFiHHG#introduction"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-link"
          id="postman-doc"
        >
          Postman Documentation
        </a>
      </div>

      <h2>Members</h2>
      <div className="container d-flex flex-wrap gap-3 justify-content-center">
        <AboutCard
          key="raina"
          member={members[0]}
          image={Raina}
          responsibility="Frontend"
          about={members[0]?.about}
        />
        <AboutCard
          key="aishwarya"
          member={members[1]}
          image={Aishwarya}
          responsibility="Backend"
          about={members[1]?.about}
        />
        <AboutCard
          key="beau"
          member={members[2]}
          image={Beau}
          responsibility="Backend"
          about={members[2]?.about}
        />
      </div>
    </div>
  );
}

export default AboutPage;
